import React, { useEffect, useRef, useState } from "react";

interface RightSideCardShellProps {
  children: React.ReactNode | ((requestClose: () => void) => React.ReactNode);
  onClose: () => void;
  widthClassName?: string;
  heightClassName?: string;
  panelStyle?: React.CSSProperties;
  side?: "left" | "right";
}

const CLOSE_DURATION_MS = 360;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const RightSideCardShell: React.FC<RightSideCardShellProps> = ({
  children,
  onClose,
  widthClassName = "w-[350px]",
  heightClassName = "h-[530px]",
  panelStyle,
  side = "right",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const isClosingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const requestClose = () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;

    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
    }

    setIsVisible(false);
    closeTimeoutRef.current = window.setTimeout(() => {
      onCloseRef.current();
    }, CLOSE_DURATION_MS);
  };

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;

    const frameId = window.requestAnimationFrame(() => {
      setIsVisible(true);
      const firstFocusable = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      firstFocusable?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        requestClose();
        return;
      }

      if (event.key === "Tab" && panelRef.current) {
        const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("keydown", handleKeyDown);
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
      }
      previousFocusRef.current?.focus();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      style={{ pointerEvents: "auto" }}
      onClick={requestClose}
    >
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isVisible ? "opacity-55" : "opacity-0"
        }`}
      />
      <div className={`absolute inset-0 flex items-center ${side === "left" ? "justify-start" : "justify-end"} p-5 md:p-8`}>
        <div
          ref={panelRef}
          className={`${widthClassName} ${heightClassName} transition-all duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)]`}
          style={{
            transformOrigin: side === "left" ? "center left" : "center right",
            transform: isVisible
              ? `translate3d(0, 0, 0) rotate(${side === "left" ? "7deg" : "-7deg"}) scale(1)`
              : `translate3d(${side === "left" ? "-110%" : "110%"}, 0, 0) rotate(${side === "left" ? "2deg" : "-2deg"}) scale(0.95)`,
            opacity: 1,
            width: "min(350px, calc(100vw - 40px))",
            height: "min(530px, calc(100vh - 56px))",
            ...panelStyle,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {typeof children === "function" ? children(requestClose) : children}
        </div>
      </div>
    </div>
  );
};

export default RightSideCardShell;
